import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('category');
  const load = () => api.get('/admin/categories').then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; try { await api.post('/admin/categories', { name, type }); setName(''); toast.success('Category added'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const del = async (id) => { try { await api.delete(`/admin/categories/${id}`); load(); } catch { toast.error('Failed'); } };
  const grouped = cats.reduce((a, c) => { (a[c.type] = a[c.type] || []).push(c); return a; }, {});
  return (
    <div>
      <h1 className="text-2xl font-semibold">Categories</h1>
      <div className="mt-4 flex flex-wrap gap-2 rounded-2xl border border-border bg-white p-4">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New name" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-category-name" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-border px-3 py-2 text-sm">{['category', 'room', 'style', 'material', 'festival', 'collection'].map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <button onClick={add} data-testid="admin-category-add" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white"><Plus size={15} /> Add</button>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {Object.entries(grouped).map(([t, list]) => (
          <div key={t} className="rounded-2xl border border-border bg-white p-4"><h2 className="mb-2 text-sm font-semibold capitalize">{t} ({list.length})</h2><div className="flex flex-wrap gap-2">{list.map((c) => <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1 text-xs">{c.name}<button onClick={() => del(c.id)}><Trash2 size={12} className="text-muted-foreground" /></button></span>)}</div></div>
        ))}
      </div>
    </div>
  );
}

export function AdminCustomers() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const load = () => api.get('/admin/customers', { params: { limit: 50 } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = async (u) => {
    try { const { data: d } = await api.get(`/admin/customers/${u.id}`); setDetail(d); }
    catch { toast.error('Could not load customer'); }
  };
  const addNote = async () => {
    const text = note.trim(); if (!text || !detail) return;
    try {
      await api.post(`/admin/customers/${detail.customer.id}/notes`, { note: text });
      const { data: fresh } = await api.get(`/admin/customers/${detail.customer.id}`);
      setDetail(fresh); setNote(''); toast.success('Note added');
    } catch { toast.error('Failed'); }
  };
  const delNote = async (nid) => {
    try {
      await api.delete(`/admin/customers/${detail.customer.id}/notes/${nid}`);
      const { data: fresh } = await api.get(`/admin/customers/${detail.customer.id}`);
      setDetail(fresh);
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold">Customers</h1>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-customers-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Orders</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {data?.items?.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3">{u.phone || '-'}</td>
                <td className="p-3">{u.order_count}</td>
                <td className="p-3"><button onClick={() => openDetail(u)} data-testid="admin-customer-view" className="rounded-full bg-[hsl(var(--primary))] px-3 py-1 text-xs font-semibold text-white">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No customers yet.</p>}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="customer-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.customer?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-2 rounded-xl bg-[hsl(var(--gaze-ivory))] p-3 sm:grid-cols-3">
                <div><p className="text-[10px] uppercase text-muted-foreground">Email</p><p className="font-medium">{detail.customer.email}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Phone</p><p className="font-medium">{detail.customer.phone || '—'}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Since</p><p className="font-medium">{new Date(detail.customer.created_at).toLocaleDateString()}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Total Orders</p><p className="font-bold">{detail.stats.total_orders}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Total Spent</p><p className="font-bold">{inr(detail.stats.total_spent)}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Last Order</p><p className="font-medium">{detail.stats.last_order_at ? new Date(detail.stats.last_order_at).toLocaleDateString() : '—'}</p></div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Orders ({detail.orders.length})</p>
                {detail.orders.length ? (
                  <ul className="mt-1 divide-y divide-border">
                    {detail.orders.slice(0, 10).map((o) => (
                      <li key={o.id} className="flex flex-wrap items-center gap-2 py-2 text-xs">
                        <span className="font-mono font-semibold">{o.order_number}</span>
                        <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 capitalize">{o.status}</span>
                        <span className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span>
                        <span className="ml-auto font-bold">{inr(o.total)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-1 text-xs text-muted-foreground">No orders yet.</p>}
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Inquiries ({detail.inquiries.length})</p>
                {detail.inquiries.length ? (
                  <ul className="mt-1 space-y-1">
                    {detail.inquiries.slice(0, 8).map((i) => (
                      <li key={i.id} className="rounded-lg bg-[hsl(var(--gaze-ivory))] p-2 text-xs">
                        <span className="font-semibold capitalize">{i.inquiry_type}</span> · {i.product_name || 'general'} · <span className="text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</span>
                        {i.message && <div className="mt-0.5 line-clamp-2 text-muted-foreground">{i.message}</div>}
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-1 text-xs text-muted-foreground">No inquiries.</p>}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Reviews ({detail.reviews.length})</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    {detail.reviews.slice(0, 5).map((r) => <li key={r.id} className="rounded-lg bg-white p-2 ring-1 ring-inset ring-border"><span className="font-semibold">{r.rating}★</span> {r.title} <span className="ml-1 text-[10px] uppercase text-muted-foreground">{r.status}</span></li>)}
                    {!detail.reviews.length && <li className="text-muted-foreground">None</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Questions ({detail.questions.length})</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    {detail.questions.slice(0, 5).map((qq) => <li key={qq.id} className="rounded-lg bg-white p-2 ring-1 ring-inset ring-border">{qq.question}</li>)}
                    {!detail.questions.length && <li className="text-muted-foreground">None</li>}
                  </ul>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Internal notes</p>
                {(detail.notes || []).length > 0 ? (
                  <ul className="mt-1 space-y-1">
                    {detail.notes.map((n) => (
                      <li key={n.id} className="flex items-start gap-2 rounded-lg bg-[hsl(var(--gaze-ivory))] p-2 text-xs">
                        <div className="flex-1"><span className="font-medium">{n.author || 'admin'}:</span> {n.text}<span className="ml-2 text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span></div>
                        <button onClick={() => delNote(n.id)} className="text-destructive"><Trash2 size={12} /></button>
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-1 text-xs text-muted-foreground">No notes yet.</p>}
                <div className="mt-2 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (e.g. VIP customer)" className="flex-1 rounded-lg border border-border px-3 py-2 text-xs" data-testid="admin-customer-note-input" />
                  <button onClick={addNote} className="rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-white" data-testid="admin-customer-note-add">Add</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const emptyForm = { code: '', type: 'percent', value: '', min_cart: '', max_discount: '', description: '', active: true, start_at: '', expires_at: '', usage_limit: '', per_user_limit: '', first_order_only: false, applies_to_products: [], applies_to_categories: [] };
  const [form, setForm] = useState(emptyForm);
  const load = () => api.get('/admin/coupons').then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, type: c.type || 'percent', value: c.value ?? '',
      min_cart: c.min_cart ?? '', max_discount: c.max_discount ?? '',
      description: c.description || '', active: c.active !== false,
      start_at: (c.start_at || '').slice(0, 10),
      expires_at: (c.expires_at || '').slice(0, 10),
      usage_limit: c.usage_limit ?? '', per_user_limit: c.per_user_limit ?? '',
      first_order_only: !!c.first_order_only,
      applies_to_products: c.applies_to_products || [],
      applies_to_categories: c.applies_to_categories || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code || form.value === '') { toast.error('Code and value are required'); return; }
    const payload = {
      code: form.code.toUpperCase().trim(),
      type: form.type,
      value: Number(form.value),
      min_cart: Number(form.min_cart || 0),
      max_discount: form.max_discount === '' ? null : Number(form.max_discount),
      description: form.description || '',
      active: !!form.active,
      start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
      expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59').toISOString() : null,
      usage_limit: form.usage_limit === '' ? null : Number(form.usage_limit),
      per_user_limit: form.per_user_limit === '' ? null : Number(form.per_user_limit),
      first_order_only: !!form.first_order_only,
      applies_to_products: form.applies_to_products,
      applies_to_categories: form.applies_to_categories,
    };
    try {
      if (editing) await api.put(`/admin/coupons/${editing.id}`, payload);
      else await api.post('/admin/coupons', payload);
      toast.success(editing ? 'Coupon updated' : 'Coupon created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this coupon?')) return; try { await api.delete(`/admin/coupons/${id}`); load(); } catch { toast.error('Failed'); } };

  const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Coupons</h1>
          <p className="text-sm text-muted-foreground">Percentage / flat discounts with expiry, usage limits and targeting.</p>
        </div>
        <button onClick={openNew} data-testid="admin-coupon-new" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white"><Plus size={15} /> New Coupon</button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-coupons-grid">
        {coupons.map((c) => (
          <div key={c.id} className="rounded-2xl border border-dashed border-[hsl(var(--gaze-gold))]/50 bg-white p-5">
            <div className="flex items-center justify-between">
              <span className="font-display text-xl">{c.code}</span>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} className="grid h-7 w-7 place-items-center rounded-lg border border-border"><Pencil size={12} /></button>
                <button onClick={() => del(c.id)} className="grid h-7 w-7 place-items-center rounded-lg border border-border text-destructive"><Trash2 size={12} /></button>
              </div>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{c.description || `${c.type === 'percent' ? c.value + '% off' : inr(c.value) + ' off'}`}</p>
            <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
              {!c.active && <span className="rounded-full bg-gray-100 px-2 py-0.5">Inactive</span>}
              {c.min_cart ? <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">Min {inr(c.min_cart)}</span> : null}
              {c.max_discount ? <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">Max {inr(c.max_discount)}</span> : null}
              {c.usage_limit ? <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">{c.usage_count || 0}/{c.usage_limit} used</span> : (c.usage_count ? <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">{c.usage_count} uses</span> : null)}
              {c.per_user_limit ? <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5">{c.per_user_limit}/user</span> : null}
              {c.first_order_only && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">First order</span>}
              {c.expires_at && <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700">Exp {String(c.expires_at).slice(0, 10)}</span>}
            </div>
          </div>
        ))}
        {coupons.length === 0 && <p className="col-span-full rounded-2xl border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground">No coupons yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit coupon' : 'New coupon'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="text-xs font-medium">Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={field} data-testid="admin-coupon-code" /></div>
            <div><label className="text-xs font-medium">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={field}><option value="percent">% off</option><option value="flat">Flat ₹</option></select></div>
            <div><label className="text-xs font-medium">Value</label><input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={field} data-testid="admin-coupon-value" /></div>
            <div><label className="text-xs font-medium">Min cart (₹)</label><input type="number" value={form.min_cart} onChange={(e) => setForm({ ...form, min_cart: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Max discount (₹)</label><input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} className={field} placeholder="Unlimited" /></div>
            <div><label className="text-xs font-medium">Usage limit (total)</label><input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className={field} placeholder="Unlimited" /></div>
            <div><label className="text-xs font-medium">Per-user limit</label><input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} className={field} placeholder="Unlimited" /></div>
            <div><label className="text-xs font-medium">Start at</label><input type="date" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Expires at</label><input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={field} /></div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.first_order_only} onChange={(e) => setForm({ ...form, first_order_only: e.target.checked })} /> First order only</label>
            </div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Applies to categories (comma-separated slugs, empty = all)</label><input value={(form.applies_to_categories || []).join(', ')} onChange={(e) => setForm({ ...form, applies_to_categories: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} className={field} placeholder="e.g. wall-art, vases" /></div>
          </div>
          <button onClick={save} data-testid="admin-coupon-save" className="mt-4 w-full rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white">{editing ? 'Update' : 'Create'} coupon</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
